import * as React from 'react';
import ReactDOM from 'react-dom';
import * as _ from 'lodash';
import ABaseColumn, {IBaseColumnProps, IBaseColumnState, IColumnDefinition, IFilterRendererProps, Filter} from './BaseColumn';
import {Column} from '../components/BasicDataGrid';
import DerivateOptionBar, {IDerivateStateBarProps, TDerivateStates, EOptionState} from './DerivateOptionBar';

interface IDerivateOptionColumnCommon {
    /** list of all possible derivates */
    derivates: Map<number, string>;

    /** derivates, which should not be shown
     *
     * @default: empty
     */
    hideDerivates?: Array<string>;

    /**
     * Defines if the derivates can be edited to be hidden or not.
     * If true, the hidden derivates will be shown too, but with 'line-through'.
     * @default false
     */
    editDerivates?: boolean;

    /**
     * defines if the options with only n.A. should be shown or not
     * @default true
     */
    showNotAvailableOptions?: boolean;

    /** width of one state icon, the value is relation to padding
     *  the real width of whole bar can be set by width
     * @default 8
     */
    iconWidth?: number;
    /** space around an each state icon, the value is relation to iconWidth
     * @default 2
     */
    padding?: number;

    /**
     * called if the visibility of a derivate was changed
     * @param derivate derivate, which visibility was changed
     * @param visible visible or hidden
     */
    onChangeDerivateVisible?(derivate: string, visible: boolean): void;
}

/** column definition for DerivateOptionColumn */
export interface DerivateOptionColumnDefinition extends Column, IDerivateOptionColumnCommon {
}

/** column definition, use internally and in BasicDataGrid */
export interface IDerivateOptionColumnDefinition extends IColumnDefinition, IDerivateOptionColumnCommon {
}

/** this properties will be set by BasicDataGrid */
interface IDerivateOptionColumnProps extends IBaseColumnProps {
    /** information about the column, filled only in edit mode */
    column?: IDerivateOptionColumnDefinition;
}

interface IDerivateOptionColumnState extends IBaseColumnState {
    column: IDerivateOptionColumnDefinition;
    value: TDerivateStates;
}

interface IDerivateOptionFilterRendererProps extends IFilterRendererProps {
    column: IDerivateOptionColumnDefinition;
}
/** Filter for derivate options */
class DerivateOptionFilter extends React.Component<IDerivateOptionFilterRendererProps, IDerivateOptionColumnState>
{
    constructor(props: IDerivateOptionFilterRendererProps) {
        super(props);

        this.state =
            {
                column: props.column,
                rowData: {},
                value: {}
            };

        this.onChangeValue = this.onChangeValue.bind(this);
    }

    componentWillReceiveProps(nextProps: Readonly<IDerivateOptionFilterRendererProps>) {
        if (nextProps.column.hideDerivates !== this.props.column.hideDerivates && nextProps.column.hideDerivates) {
            const hideDerivates: Array<string> = nextProps.column.hideDerivates;
            let derivateStates = {...this.state.value};
            const derivates = Object.keys(derivateStates);
            derivates.forEach(derivate => {
                if (_.findIndex(hideDerivates, hideValue => hideValue === derivate) >= 0) {
                    delete derivateStates[derivate];
                }
            });
            const filter: Filter = {column: this.props.column, filterTerm: derivateStates};
            this.setState({value: derivateStates}, () => this.props.onChange(filter));
        }
    }

    onChangeValue(derivate: string, state: EOptionState) {
        // check if derivate is not in list of hideDerivates
        if (this.isDerivateVisible(derivate)) {
            let derivateStates = {...this.state.value};
            if (state === EOptionState.NotAvailable) {
                delete derivateStates[derivate];
            }
            else {
                derivateStates[derivate] = state;
            }
            const filter: Filter = {column: this.props.column, filterTerm: derivateStates};
            this.setState({value: derivateStates}, () => this.props.onChange(filter));
        }
    }

    /**
     * checks if the derivate is not in list of hideDerivates
     * @param derivate derivate to be checked
     * @returns derivate is not in list of hideDerivates
     */
    isDerivateVisible(derivate: string): boolean {
        const hideDerivates = this.props.column.hideDerivates ? this.props.column.hideDerivates : [];
        return _.findIndex(hideDerivates, hideValue => hideValue === derivate) < 0;
    }

    render() {
        const derivates = Array.from(this.props.column.derivates.values()).filter(derivate => this.props.column.editDerivates ? true : this.isDerivateVisible(derivate));

        let derivateStates: TDerivateStates = {};
        derivates.forEach(derivate => {
            derivateStates[derivate] = this.isDerivateVisible(derivate) ? this.state.value[derivate] : EOptionState.Hidden;
        });
        let props: IDerivateStateBarProps = {derivates, derivateStates};
        Object.assign(props, this.props.column);
        // reassign, because column can override the props
        props.derivates = derivates;
        props.derivateStates = derivateStates;

        return (
            <DerivateOptionBar
                {...props}
                editMode={true}
                onChange={this.onChangeValue}
            />
        );
    }
}

/** shows assignment of option state to derivate */
export default class DerivateOptionColumn extends ABaseColumn<IDerivateOptionColumnProps, IDerivateOptionColumnState>
{
    static get filterRenderer(): React.ComponentClass<IDerivateOptionFilterRendererProps> {
        return DerivateOptionFilter;
    }

    /**
     * a column specific method to check data be valid on filter or not. Overwrite it to have other check.
     * @param filter filter
     * @param rowData row data to be checked for validness after filtering
     * @returns true if data is not filtered out, false otherwise
     */
    static filterValid(filter: Filter, rowData: any): boolean {
        const value: TDerivateStates = rowData[filter.column.key];
        const filterTerm: TDerivateStates = filter.filterTerm;
        let result: boolean = true;

        for (let derivate in filterTerm) {
            if (!filterTerm.hasOwnProperty(derivate))
                continue;

            const stateFilter: EOptionState = filterTerm[derivate];
            const stateValue: EOptionState = value.hasOwnProperty(derivate) ? value[derivate] : EOptionState.NotAvailable;

            if (stateValue !== stateFilter) {
                result = false;
                break;
            }
        }
        return result;
    }

    constructor(props: IDerivateOptionColumnProps) {
        super(props);

        // bind to this
        this.onChangeValue = this.onChangeValue.bind(this);
    }

    getInputNode(): Element | any {
        // overwrite it because of stateless component cann't be set refs
        return ReactDOM.findDOMNode(this);
    }

    onChangeValue(derivate: string, state: EOptionState): void {
        // check if derivate is not in list of hideDerivates
        if (this.isDerivateVisible(derivate) && state !== EOptionState.Hidden) {
            let derivateStates = {...this.state.value};
            derivateStates[derivate] = state;
            this.onChange(derivateStates);
        }
    }

    /**
     * checks if the derivate is not in list of hideDerivates
     * @param derivate derivate to be checked
     * @returns derivate is not in list of hideDerivates
     */
    isDerivateVisible(derivate: string): boolean {
        const hideDerivates = this.state.column.hideDerivates ? this.state.column.hideDerivates : [];
        return _.findIndex(hideDerivates, hideValue => hideValue === derivate) < 0;
    }

    /**
     * custom rendering of value part
     * @param editMode rendering in edit mode or not
     */
    renderCustom(editMode: boolean) {
        const derivates = Array.from(this.state.column.derivates.values()).filter(derivate => this.state.column.editDerivates ? true : this.isDerivateVisible(derivate));
        const validStates = Object.keys(EOptionState);
        let derivateStates: TDerivateStates = {};
        derivates.forEach(derivate => {
            let derState = this.isDerivateVisible(derivate) ? this.state.value[derivate] : EOptionState.Hidden;
            if (derState === undefined) {
                derState = EOptionState.NotAvailable;
            }

            if (_.findIndex(validStates, state => derState.toString() === state) < 0) {
                derState = EOptionState.Hidden;
            }
            derivateStates[derivate] = derState;
        });

        let props: IDerivateStateBarProps = {derivates, derivateStates};
        Object.assign(props, this.state.column);
        // reassign, because column can override the props
        props.derivates = derivates;
        props.derivateStates = derivateStates;

        return (
            <DerivateOptionBar
                {...props}
                key={this.getId()}
                editMode={editMode}
                onChange={this.onChangeValue}
            />
        );
    }
}

interface IDerivateOptionColumnHeaderProps {
    column: IDerivateOptionColumnDefinition;
}

/** renders the column header for derivate option column to fit into header cell */
export class DerivateOptionColumnHeaderRenderer extends React.Component<IDerivateOptionColumnHeaderProps>
{
    constructor(props: IDerivateOptionColumnHeaderProps) {
        super(props);

        this.handleClick = this.handleClick.bind(this);
        this.isDerivateVisible = this.isDerivateVisible.bind(this);
    }

    handleClick(derivate: string) {
        if (this.props.column.editDerivates && this.props.column.onChangeDerivateVisible) {
            this.props.column.onChangeDerivateVisible(derivate, !this.isDerivateVisible(derivate));
        }
    }

    /**
     * checks if the derivate is not in list of hideDerivates
     * @param derivate derivate to be checked
     * @returns derivate is not in list of hideDerivates
     */
    isDerivateVisible(derivate: string): boolean {
        const hideDerivates = this.props.column.hideDerivates ? this.props.column.hideDerivates : [];
        return _.findIndex(hideDerivates, hideValue => hideValue === derivate) < 0;
    }

    render() {
        // show hidden only if edit derivates
        const derivates = Array.from(this.props.column.derivates.values()).filter(derivate => this.props.column.editDerivates ? true : this.isDerivateVisible(derivate));
        return (
            <div style={{width: '100%', height: '100%'}}>
                <div >
                    <div style={{transform: 'rotate(-90deg)'}}>
                        <div>
                            {
                                derivates.map((value: string, index: number) => {
                                    return <div key={value} onClick={() => this.handleClick(value)} >{value}</div>;
                                })
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/DerivateOptionColumn.tsx